/**
 * SDK-owned sessions for the dashboard.
 *
 * From 2026-08-24, Supabase's own SDK manages the entire cookie and session
 * lifecycle: the session lives in the default `sb-<project-ref>-auth-token`
 * cookie, written, validated, refreshed, and cleared by `@supabase/ssr` —
 * nothing of ours names, signs, parses, or rotates an auth cookie any more.
 * The decision trail is in the roadmap under "Sessions, revised".
 *
 * What reading a session actually does here:
 *
 *   1. `auth.getUser()` — the SDK validates the token upstream and, when it
 *      has lapsed, spends the refresh token and re-issues cookies itself.
 *   2. The verified Supabase UUID is resolved to the LIVE MySQL account
 *      (`requireLiveDashboardAccountByAuthId`), fail-closed: deleted,
 *      disabled, role-less, or unlinked rows produce no session. This is
 *      the sub invariant now — the SQL row is read fresh on every session
 *      resolution, so role and permission changes bite immediately and no
 *      claim mirroring exists.
 *
 * What stayed ours, deliberately, because it is authorization rather than
 * session management: the live-account gate above, the
 * `sessions_valid_after` revocation stamp (checked a layer up in
 * `dashboardAuthApi` against the token's `iat`), the role gate, and the
 * local-dev bypass. Session LENGTH is Supabase's default policy now — the
 * 14/30-day fixed deadline and the Remember Me choice retired with the
 * custom cookies; a native time-boxed session awaits the Pro plan and is
 * recorded in the roadmap as such.
 */
import { utcUnixMs } from "@dashboard-config"
import type { SupabaseClient } from "@supabase/supabase-js"
import { decodeJwt } from "jose"
import type { AuthUser, DashboardRole } from "../dashboardRoles"
import { hasDashboardRole } from "../dashboardRoles"
import { createDashboardSupabaseServerClient, requestCookieMethods } from "./supabaseSsrClient"

/** Legacy custom cookie names — kept only so cutover can clear them. */
export const DASHBOARD_SESSION_COOKIE = "playbrain_dashboard_session"
export const DASHBOARD_REFRESH_COOKIE = "playbrain_dashboard_refresh"

export type DashboardSession = {
  user: AuthUser
  /** The access token's `iat` — what revocation compares against. */
  createdAt: number
  lastSeenAt: number
  /** The access token's `exp`; the SDK refreshes past it on its own. */
  expiresAt: number
  token: string
}

export type DashboardAuthResult = { user: AuthUser; session: DashboardSession } | { response: Response }

export type SessionReadDeps = {
  /** Tests inject a fake SDK client; production builds one per request. */
  client?: SupabaseClient
  /** Tests inject the account resolver; production imports the live one. */
  resolveAccount?: (authId: string) => Promise<AuthUser | null>
}

export function dashboardSupabaseClientForRequest(request: Request): SupabaseClient {
  return createDashboardSupabaseServerClient({ cookies: requestCookieMethods(request) })
}

/**
 * Reads the session: SDK validation first, live SQL account second. Returns
 * null for anything less than a fully live, linked, role-carrying account —
 * a session that cannot be authorized must not exist.
 */
export async function readDashboardSession(
  request: Request,
  now = utcUnixMs(),
  deps: SessionReadDeps = {}
): Promise<DashboardSession | null> {
  const client = deps.client ?? dashboardSupabaseClientForRequest(request)

  const { data: userData, error } = await client.auth.getUser()
  if (error || !userData?.user) return null

  const { data: sessionData } = await client.auth.getSession()
  const accessToken = sessionData?.session?.access_token
  if (!accessToken) return null

  /** Parse only — `getUser` above already validated with the auth server. */
  let issuedAt = 0
  let expiresAt = 0
  try {
    const claims = decodeJwt(accessToken)
    issuedAt = (claims.iat ?? 0) * 1000
    expiresAt = (claims.exp ?? 0) * 1000
  } catch {
    return null
  }

  const resolve = deps.resolveAccount ?? liveAccountResolver
  const user = await resolve(userData.user.id)
  if (!user) return null

  return { user, createdAt: issuedAt, lastSeenAt: now, expiresAt, token: accessToken }
}

export type RefreshedDashboardSession = {
  session: DashboardSession
  /** Always empty now: the SDK writes rotated cookies straight to the response context. */
  setCookies: string[]
}

/**
 * Kept for the session endpoints' call shape. Refresh is no longer a separate
 * act — `getUser()` inside the read spends the refresh token itself when the
 * access token has lapsed, and the adapter attaches the rotated cookies.
 */
export async function readOrRefreshDashboardSession(
  request: Request,
  now = utcUnixMs(),
  deps: SessionReadDeps = {}
): Promise<RefreshedDashboardSession | null> {
  const session = await readDashboardSession(request, now, deps)
  return session ? { session, setCookies: [] } : null
}

/**
 * Ends the session: the SDK revokes the refresh token upstream and deletes
 * its own cookies through the adapter. The returned strings clear only the
 * LEGACY custom cookies, so a browser that predates the cutover is swept
 * clean on its first logout.
 */
export async function clearDashboardSessionCookies(
  request?: Request,
  deps: SessionReadDeps = {}
): Promise<string[]> {
  if (request) {
    const client = deps.client ?? dashboardSupabaseClientForRequest(request)
    try {
      await client.auth.signOut({ scope: "local" })
    } catch {
      // Provider unreachable: cookie deletion below still signs them out here.
    }
  }
  return [
    `${DASHBOARD_SESSION_COOKIE}=; Max-Age=0; Path=/; HttpOnly; SameSite=Lax`,
    `${DASHBOARD_REFRESH_COOKIE}=; Max-Age=0; Path=/api/dashboard/auth; HttpOnly; SameSite=Lax`,
  ]
}

export async function requireDashboardCookieAuth(
  request: Request,
  roles: DashboardRole[] = ["admin", "editor", "viewer"],
  deps: SessionReadDeps = {}
): Promise<DashboardAuthResult> {
  const session = await readDashboardSession(request, utcUnixMs(), deps)
  if (!session) {
    if (allowLocalDevBypass()) {
      const now = utcUnixMs()
      const user: AuthUser = {
        sub: "local-dev",
        dashboardLanguage: "en",
        email: "local-dev@playbrain.com",
        name: "Local Dev",
        username: "Local Dev",
        role: "admin",
        roles: ["admin"],
      }
      return {
        user,
        session: {
          token: "local-dev",
          user,
          createdAt: now,
          lastSeenAt: now,
          expiresAt: now + 60 * 60 * 1000,
        },
      }
    }
    return { response: Response.json({ error: "Missing dashboard session" }, { status: 401 }) }
  }

  if (roles.length > 0 && !hasDashboardRole(session.user, roles)) {
    return { response: Response.json({ error: "Insufficient dashboard role" }, { status: 403 }) }
  }

  return { user: session.user, session }
}

export function parseCookieHeader(header: string | null): Record<string, string> {
  const cookies: Record<string, string> = {}
  if (!header) return cookies
  for (const part of header.split(";")) {
    const [name, ...valueParts] = part.trim().split("=")
    if (!name) continue
    const value = valueParts.join("=")
    try {
      cookies[name] = decodeURIComponent(value)
    } catch {
      // A cookie we never wrote can hold undecodable junk; keep it raw
      // rather than letting one bad cookie 500 the whole request.
      cookies[name] = value
    }
  }
  return cookies
}

export function dashboardSessionPublicPayload(session: DashboardSession) {
  return {
    user: session.user,
    expiresAt: session.expiresAt,
  }
}

export function dashboardSessionPublicPayloadWithUser(session: DashboardSession, user: AuthUser) {
  return {
    ...dashboardSessionPublicPayload(session),
    user,
  }
}

/**
 * The production account resolver, imported across the app boundary the same
 * way api-cms reaches api-users. Fail-closed by construction: everything
 * short of a live, linked, role-carrying row resolves to null.
 */
async function liveAccountResolver(authId: string): Promise<AuthUser | null> {
  /**
   * Through the seam, never the `.server` module: this file sits in the
   * client-reachable route graph (dashboard.tsx reads sessions in its
   * loader), and naming the `.server` file from here pulls TypeORM's browser
   * build into the client bundle — the expo-sqlite failure the module
   * headers warn about. `createServerOnlyFn` is the boundary that keeps the
   * import on the server side of the bundle split.
   */
  const { requireLiveDashboardAccountByAuthId } = await import(
    "../../../apps/api-cms/src/features/dashboard-profile/dashboardProfile"
  )
  return requireLiveDashboardAccountByAuthId(authId)
}

function allowLocalDevBypass() {
  return (
    isLocalDevLikeStage(process.env.PUBLIC_STAGE) &&
    readBooleanEnv(process.env.DASHBOARD_AUTH_BYPASS) &&
    !process.env.SUPABASE_PROJECT_ID
  )
}

function isLocalDevLikeStage(stage: string | undefined) {
  return !stage || stage === "local" || stage === "dev"
}

function readBooleanEnv(value: string | undefined) {
  if (!value) return false
  return ["1", "true", "yes", "on"].includes(value.toLowerCase())
}
